import { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import "../../i18n";
import { Notice } from "../../design/components";
import { useDocuments, useStageCounts } from "../../api/queries";
import { useHistory } from "../../api/history";
import type { DocumentFilters } from "../../api/types";
import { ExportHistoryButton, isHistoryQuery } from "./HistoryFilters";
import { QueueFilters, type QueueFilterState } from "./QueueFilters";
import { DocumentRow } from "./DocumentRow";
import {
  isQueueFiltered,
  mergeQueueFilters,
  readQueueFilters,
  writeQueueFilters,
} from "./queueFilterState";
import styles from "./DocumentsQueue.module.css";

const PAGE_SIZE = 50;

function readFilters(params: URLSearchParams): QueueFilterState {
  return { ...readQueueFilters(params), entityId: params.get("entityId") ?? "" };
}

function writeFilters(filters: QueueFilterState): URLSearchParams {
  const params = writeQueueFilters(filters);
  if (filters.entityId) params.set("entityId", filters.entityId);
  return params;
}

function isFiltered(filters: QueueFilterState): boolean {
  return isQueueFiltered(filters) || Boolean(filters.entityId);
}

export function DocumentsQueue() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openRowId, setOpenRowId] = useState<string | null>(null);

  const filters = useMemo(() => readFilters(searchParams), [searchParams]);
  const paused = openRowId !== null;

  const query = useMemo<DocumentFilters>(
    () => ({
      direction: filters.direction,
      stage: filters.stage.join(","),
      state: filters.state,
      entityId: filters.entityId,
      from: filters.from,
      to: filters.to,
      q: filters.q,
      family: filters.family,
      typeCode: filters.typeCode.join(","),
      limit: PAGE_SIZE,
    }),
    [filters],
  );

  // A history-only filter is served by /api/history; everything else stays on the queue.
  const asHistory = isHistoryQuery(filters);
  const documents = useDocuments(query, paused, !asHistory);
  const history = useHistory(query, asHistory);
  const list = asHistory ? history : documents;
  const counts = useStageCounts({ direction: filters.direction, entityId: filters.entityId }, paused);

  const patch = useCallback(
    (change: Partial<QueueFilterState>) => {
      setSearchParams(writeFilters(mergeQueueFilters(filters, change)), { replace: true });
    },
    [filters, setSearchParams],
  );

  const clear = useCallback(() => setSearchParams(new URLSearchParams(), { replace: true }), [setSearchParams]);

  const onActionsOpenChange = useCallback((id: string, open: boolean) => {
    setOpenRowId((current) => (open ? id : current === id ? null : current));
  }, []);

  // Both /api/documents and /api/history narrow on family and typeCode, so the rows
  // arrive filtered and an exported CSV holds exactly what is on screen.
  const rows = useMemo(
    () => (list.data?.pages ?? []).flatMap((page) => page.items),
    [list.data],
  );
  const filtered = isFiltered(filters);

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1 className="display">{t("documents.queue.title", "Documents")}</h1>
          <p className={styles.lede}>
            {t("documents.queue.subtitle", "Every document in flight and finished, across the group.")}
          </p>
        </div>
        <ExportHistoryButton filters={query} />
      </div>

      <section className="card card-tight">
        <div className="card-head wrap">
          <div className={styles.filters}>
            <QueueFilters
              filters={filters}
              onChange={patch}
              onClear={clear}
              hasFilters={filtered}
              stageCounts={counts.data}
            />
          </div>
        </div>

        {list.isError ? (
          <div className="card-body">
            <Notice kind="fail">{t("documents.queue.loadError", "The document queue could not be loaded.")}</Notice>
          </div>
        ) : rows.length === 0 && !list.isLoading ? (
          <div className="card-body">
            <Notice kind="info">
              {filtered ? (
                <div className="row">
                  <span className="grow">{t("documents.queue.noMatches", "No documents match these filters")}</span>
                  <button type="button" className="btn btn-sm btn-ghost" onClick={clear}>
                    {t("documents.queue.clearFilters", "Clear filters")}
                  </button>
                </div>
              ) : (
                t("documents.queue.empty", "No documents yet for this group")
              )}
            </Notice>
          </div>
        ) : (
          <>
            <div className={`table-wrap ${styles.tableScroll}`}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th>{t("documents.queue.columns.company", "Company")}</th>
                    <th>{t("documents.queue.columns.document", "Document")}</th>
                    <th>{t("documents.queue.columns.type", "Type")}</th>
                    <th>{t("documents.queue.columns.counterparty", "Counterparty")}</th>
                    <th>{t("documents.queue.columns.stage", "Stage")}</th>
                    <th>{t("documents.queue.columns.status", "Status")}</th>
                    <th className="r">{t("documents.queue.columns.attempts", "Attempts")}</th>
                    <th className="r">{t("documents.queue.columns.value", "Value")}</th>
                    <th className="nowrap">{t("documents.queue.columns.updated", "Updated")}</th>
                    <th aria-label={t("documents.queue.columns.actions", "Actions")} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <DocumentRow
                      key={row.id}
                      row={row}
                      onActionsOpenChange={(open) => onActionsOpenChange(row.id, open)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            {list.hasNextPage ? (
              <div className="card-body">
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => void list.fetchNextPage()}
                  disabled={list.isFetchingNextPage}
                >
                  {list.isFetchingNextPage
                    ? t("documents.queue.loading", "Loading…")
                    : t("documents.queue.loadMore", "Load more")}
                </button>
              </div>
            ) : null}
          </>
        )}
      </section>
    </>
  );
}
